/**
 * PrizeTableEditor — modal editor for the Scratch Card weighted prize
 * distribution.
 *
 * Design contract
 * ---------------
 *   * Full-table CRUD (add / remove / edit multiplier + weight per row).
 *   * Live-computed `Effective RTP` at the bottom of the modal — the
 *     admin's north-star metric.
 *   * Live weight-total counter — must equal `TOTAL_WEIGHT` (10000)
 *     before Save is enabled. This is a hard invariant of the game's
 *     provably-fair HMAC modulus (see `games/scratch_card.py` —
 *     `MAX_ROLL = 10_000`). We do NOT try to normalize weights on the
 *     server because that would break `/verify` reproducibility for
 *     stored rounds. Instead, we surface the rule + a one-click
 *     "Rebalance to 10000" affordance that scales weights
 *     proportionally.
 *   * Warn-only RTP banner: turns red when computed RTP is outside
 *     88..98%. Save is not blocked (spec: admin owns the economics).
 *   * Cancel discards the working copy; Save posts the whole array
 *     via PATCH /api/admin/games/scratch-card (payload validated
 *     server-side, all-or-nothing).
 *
 * Not in scope (intentionally)
 *   * Multi-currency prize tables — a single table drives all four
 *     currencies today. Splitting would require duplicating
 *     `settled_multiplier` semantics per currency; deferred until
 *     ops actually asks for it.
 *   * Client-side "rebalance to hit target RTP X" — probably a
 *     phase-2 nice-to-have.
 */
import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { extractError } from "../../lib/formatError";

const TOTAL_WEIGHT = 10_000;
const RTP_MIN = 88;
const RTP_MAX = 98;

function computeRtp(rows) {
  const totalW = rows.reduce((s, r) => s + (Number(r.weight) || 0), 0);
  if (totalW <= 0) return 0;
  const weighted = rows.reduce(
    (s, r) => s + (Number(r.multiplier) || 0) * (Number(r.weight) || 0),
    0,
  );
  return (weighted / totalW) * 100;
}

function isValidRow(row) {
  const m = Number(row.multiplier);
  const w = Number(row.weight);
  return Number.isFinite(m) && m >= 0 && Number.isInteger(w) && w >= 1;
}

export default function PrizeTableEditor({ open, initialTable, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  // Reset the working copy every time the modal opens so previous
  // edits don't leak across opens.
  useEffect(() => {
    if (open) {
      setRows(
        (initialTable || []).map((r) => ({
          multiplier: r.multiplier,
          weight: r.weight,
        })),
      );
    }
  }, [open, initialTable]);

  const totalWeight = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.weight) || 0), 0),
    [rows],
  );
  const rtp = useMemo(() => computeRtp(rows), [rows]);
  const weightsOk = totalWeight === TOTAL_WEIGHT;
  const rowsValid = rows.length > 0 && rows.every(isValidRow);
  const rtpOutOfRange = rtp < RTP_MIN || rtp > RTP_MAX;

  if (!open) return null;

  const patchRow = (idx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, { multiplier: 0, weight: 1 }]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const rebalance = () => {
    // Scale each weight proportionally so the sum lands on TOTAL_WEIGHT.
    // Uses the largest-remainder method to keep every weight integral
    // while preserving the total exactly. Rows with weight=0 stay at 0
    // (admin's explicit call to keep a tier disabled).
    const sum = rows.reduce((s, r) => s + (Number(r.weight) || 0), 0);
    if (sum <= 0) {
      toast.error("At least one row must have weight > 0 before rebalancing");
      return;
    }
    const scaled = rows.map((r) => {
      const w = Number(r.weight) || 0;
      const exact = (w / sum) * TOTAL_WEIGHT;
      return { row: r, floor: Math.floor(exact), remainder: exact - Math.floor(exact) };
    });
    let remaining = TOTAL_WEIGHT - scaled.reduce((s, x) => s + x.floor, 0);
    // Distribute the leftover units to the rows with the biggest
    // fractional remainder — canonical largest-remainder rounding.
    const order = [...scaled.keys()].sort((a, b) => scaled[b].remainder - scaled[a].remainder);
    for (const i of order) {
      if (remaining <= 0) break;
      scaled[i].floor += 1;
      remaining -= 1;
    }
    setRows(scaled.map((x) => ({ multiplier: x.row.multiplier, weight: x.floor })));
    toast.success("Weights rebalanced to sum to 10,000");
  };

  const save = async () => {
    if (!rowsValid) {
      toast.error("Every row needs multiplier ≥ 0 and weight ≥ 1");
      return;
    }
    if (!weightsOk) {
      toast.error(`Weights sum to ${totalWeight}, must be ${TOTAL_WEIGHT}`);
      return;
    }
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        multiplier: Number(r.multiplier),
        weight: Number(r.weight),
      }));
      const { data } = await api.patch("/admin/games/scratch-card", { prize_table: payload });
      toast.success(`Prize table saved · Effective RTP ${data.effective_rtp_percent.toFixed(2)}%`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <button
        className="fixed inset-0 z-40 bg-black/70"
        onClick={onClose}
        aria-label="Close editor"
        data-testid="prize-table-editor-backdrop"
      />

      {/* Modal panel */}
      <div
        className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,640px)] max-h-[90vh] flex flex-col rounded-2xl border border-[color:var(--line)] bg-[color:var(--shell-2)] shadow-2xl"
        data-testid="prize-table-editor"
      >
        <div className="px-6 pt-5 pb-3 border-b border-[color:var(--line)]">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)]">
            Scratch Card
          </div>
          <div className="font-display italic text-2xl text-[color:var(--ink)] mt-0.5">
            Prize table editor
          </div>
          <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed">
            Weights must sum to <b>10,000</b> (provably-fair modulus).
            Effective RTP is Σ(weight × multiplier) ÷ 10,000.
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-widest uppercase text-[color:var(--muted)]">
              <tr>
                <th className="text-left font-normal pb-2 w-1/3">Multiplier ×</th>
                <th className="text-left font-normal pb-2 w-1/3">Weight (out of 10,000)</th>
                <th className="text-left font-normal pb-2">Probability</th>
                <th className="pb-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const w = Number(row.weight) || 0;
                const prob = totalWeight > 0 ? (w / totalWeight) * 100 : 0;
                const invalid = !isValidRow(row);
                return (
                  <tr
                    key={i}
                    data-testid={`prize-row-${i}`}
                    className="border-t border-[color:var(--line)]/40"
                  >
                    <td className="py-2 pr-2">
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        value={row.multiplier}
                        onChange={(e) => patchRow(i, "multiplier", e.target.value)}
                        className="bet-input w-full text-sm font-mono"
                        data-testid={`prize-row-${i}-multiplier`}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <input
                        type="number"
                        step="1"
                        min={1}
                        value={row.weight}
                        onChange={(e) => patchRow(i, "weight", e.target.value)}
                        className="bet-input w-full text-sm font-mono"
                        data-testid={`prize-row-${i}-weight`}
                      />
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs text-[color:var(--muted)]">
                      {prob.toFixed(2)}%
                    </td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => removeRow(i)}
                        className="w-7 h-7 rounded-md text-[color:var(--muted)] hover:text-[color:var(--red)] hover:bg-[color:var(--red)]/10 inline-flex items-center justify-center transition"
                        data-testid={`prize-row-${i}-remove`}
                        aria-label={`Remove row ${i}`}
                        disabled={rows.length <= 1}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                    {invalid && (
                      <td colSpan={4} className="text-[10px] text-[color:var(--red)] italic pb-1">
                        Row {i}: multiplier must be ≥ 0, weight must be an integer ≥ 1.
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button
              onClick={addRow}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md border border-[color:var(--line)] text-xs uppercase tracking-widest text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--ink)] transition"
              data-testid="prize-add-row"
            >
              <Plus className="w-3 h-3" />
              Add row
            </button>
            <button
              onClick={rebalance}
              disabled={weightsOk}
              className={
                "inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs uppercase tracking-widest transition " +
                (weightsOk
                  ? "border border-[color:var(--line)] text-[color:var(--muted)]/50 cursor-not-allowed"
                  : "border border-gold/50 text-gold hover:brightness-110")
              }
              data-testid="prize-rebalance"
              title="Scale weights proportionally so they sum to 10,000"
            >
              <RotateCcw className="w-3 h-3" />
              Rebalance to 10,000
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[color:var(--line)] space-y-3">
          <div className="flex items-center justify-between text-xs">
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Weight total</span>{" "}
              <span
                className={
                  "font-mono ml-1 " +
                  (weightsOk ? "text-[color:var(--green)]" : "text-[color:var(--red)]")
                }
                data-testid="prize-weight-total"
              >
                {totalWeight.toLocaleString()} / {TOTAL_WEIGHT.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Effective RTP</span>{" "}
              <span
                className={
                  "font-display italic text-lg ml-1 " +
                  (rtpOutOfRange ? "text-[color:var(--red)]" : "text-gold")
                }
                data-testid="prize-effective-rtp"
              >
                {rtp.toFixed(2)}%
              </span>
            </div>
          </div>

          {rtpOutOfRange && (
            <div
              className="text-[11px] text-[color:var(--red)] border border-[color:var(--red)]/40 bg-[color:var(--red)]/10 rounded-md px-3 py-2 flex items-start gap-2"
              data-testid="prize-rtp-warning"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                <b>Non-standard RTP.</b> Effective RTP of {rtp.toFixed(2)}% is
                outside the recommended {RTP_MIN}–{RTP_MAX}% band. Save is
                still allowed — double-check the economics before rolling
                this out.
              </span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 h-9 rounded-md border border-[color:var(--line)] text-xs uppercase tracking-widest text-[color:var(--muted)] hover:text-[color:var(--ink)]"
              data-testid="prize-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || !weightsOk || !rowsValid}
              className={
                "px-5 h-9 rounded-md text-xs font-semibold uppercase tracking-widest transition " +
                (saving || !weightsOk || !rowsValid
                  ? "bg-[color:var(--line)] text-[color:var(--muted)] cursor-not-allowed"
                  : "bg-gradient-to-r from-[#ff3d3d] to-[#ff9138] text-white shadow-[0_0_18px_rgba(255,80,60,0.35)] hover:brightness-110")
              }
              data-testid="prize-save"
            >
              {saving ? "Saving…" : "Save prize table"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
